import React, { Dispatch, useEffect, useState } from "react";
import BootstrapTable, { ColumnDescription, PaginationOptions, SelectRowProps } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { useDispatch, useSelector } from "react-redux";
import { FileFormatIcon } from "src/assets/images/svg/FileIconCollection";
import { ArchiveConstants, DEFAULT_PAGE_SIZE } from "src/Core/Common/Constants";
import { MessageSource, MyFilesFileType } from "src/Core/Common/Enums";
import { IMyFilesFileModel } from "src/Core/ViewModels/File/MyFilesFileModel";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { ApplicationState } from "src/store";
import Checkbox, { CheckBoxSize } from "../Common/ui/Checkbox";
import { Button, Dropdown, DropdownMenu, DropdownToggle, Popover } from "react-bootstrap";
import { getFileExtension } from "../Compose/Helper/HelperFunctions";
import { getFileViewLoaderHeight, getFileViewWrapperHeight } from "src/helper/HelperFunctions";
import FileActionsPopUp from "../Message/Common/FileActionPopUp";

interface IArchiveFileTableProps {
    onMessageDownloadSingleAttachment?: (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        messageType?: number
    ) => void;
    onDRDownloadSingleAttachment?: (fileGuid: string, sentDate: Date, fileName: string) => void;
    selectedFiles: IMyFilesFileModel[];
    setSelectedFiles: Dispatch<React.SetStateAction<IMyFilesFileModel[]>>;
    currentFolder: IFolderModel;
    currentFilesPageNumber?: number;
    setCurrentPageNumber: Dispatch<React.SetStateAction<number>>;
    sortBy?: any;
    setSortBy?: (fileViewSort: any) => void;
}

interface FileViewColumnDescription extends ColumnDescription {
    headerTitle?: boolean | string;
}

const ArchiveFileTable: React.FC<IArchiveFileTableProps> = props => {
    const {
        onMessageDownloadSingleAttachment,
        onDRDownloadSingleAttachment,
        selectedFiles,
        setSelectedFiles,
        currentFolder,
        currentFilesPageNumber,
        setCurrentPageNumber,
    } = props;
    const dispatch = useDispatch();
    const { isLoading, fileData } = useSelector((state: ApplicationState) => state.folderState.fileViewData);

    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const [count, setCount] = useState<number>(0);
    const [selectedFileActionPopupRowId, setSelectedFileActionPopupRowId] = useState<string | null>(null);

    const paginationOptions: PaginationOptions = {
        totalSize: count,
        sizePerPage: DEFAULT_PAGE_SIZE,
        page: currentFilesPageNumber,
        showTotal: false,
        withFirstAndLast: true,
        alwaysShowAllBtns: true,
        sizePerPageList: [], // No need for size per page list as we are using default page size
    };

    useEffect(() => {
        setCount(fileData?.length ? fileData[0].count : 0);
    }, [fileData, currentFilesPageNumber]);

    const handlePageChange = (page: number) => {
        setCurrentPageNumber(page);
    };

    const fileToolbar = (cell: string, row: IMyFilesFileModel, selectedFileActionPopupRowId: any, setSelectedFileActionPopupRowId: any): JSX.Element => {
        const isOpen = selectedFileActionPopupRowId === row.rowId;

        return (
            <Dropdown
                show={isOpen}
                onToggle={(nextShow) => setSelectedFileActionPopupRowId(nextShow ? row.rowId : null)}
                key={row.rowId} drop="start" className="inline-Actions-Wrapper"
            >
                <DropdownToggle as={Button}
                    onClick={e => {
                        e.stopPropagation();
                        // Toggle open/close on click
                        setSelectedFileActionPopupRowId(isOpen ? null : row.rowId);
                    }}>
                    <i className="fas fa-ellipsis-h cursor-pointer inline-actions-trigger" />
                </DropdownToggle>
                <DropdownMenu>
                    <Dropdown.Item>
                        <FileActionsPopUp
                            selectedFile={row}
                            onHide={() => { setSelectedFileActionPopupRowId(null); }}
                            currentFolder={props.currentFolder}
                        />
                    </Dropdown.Item>
                </DropdownMenu>
            </Dropdown>
        );
    };

    const fileNameFormatter = (cell: string, row: IMyFilesFileModel, rowIndex: number, { setSelectedFileActionPopupRowId, selectedFileActionPopupRowId }): JSX.Element => {
        return (
            <div style={{ width: "inherit" }} className="table-cell-with-controls folder-table">
                <div className="flex-row">
                    <span className="margin-right-2">
                        <FileFormatIcon extension={getFileExtension(cell)} width={40} height={40}></FileFormatIcon>
                    </span>
                    <div
                        className="file-icon-name-container"
                        title={row.fileName}
                        data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDownloadFile}
                        onClick={() => {
                            if (row.fileType === MyFilesFileType.DocumentRequest) {
                                onDRDownloadSingleAttachment &&
                                    onDRDownloadSingleAttachment(row.fileGuid, row.sentOrReceivedDate, row.fileName);
                            } else {
                                onMessageDownloadSingleAttachment &&
                                    onMessageDownloadSingleAttachment(
                                        row.fileId,
                                        row.fileGuid,
                                        row.referenceId,
                                        row.fileName,
                                        row.senderEmail,
                                        row.referenceGuid,
                                        row.messageSource,
                                        row.fileType
                                    );
                            }
                        }}
                    >
                        <p className="overflowText">{row.fileName}</p>
                    </div>
                </div>
                {fileToolbar(cell, row, selectedFileActionPopupRowId, setSelectedFileActionPopupRowId)}
            </div>
        );
    };

    const headerFormatter = (column: any, colIndex: number, { sortElement }: any): JSX.Element => {
        return (
            <>
                <span title={column.text}>{column.text}</span>
                {sortElement}
            </>
        );
    };

    const columns: FileViewColumnDescription[] = [
        {
            text: "File Name",
            dataField: "fileName",
            formatter: fileNameFormatter,
            sort: true,
            headerFormatter,
            style: { width: "calc(100vw - 70px)", overflow: "initial" },
            formatExtraData: { setSelectedFileActionPopupRowId: setSelectedFileActionPopupRowId, selectedFileActionPopupRowId: selectedFileActionPopupRowId }
        },
    ];

    const setNoContent = () => {
        if (!isLoading && fileData.length === 0) {
            return ArchiveConstants.ShareFolder.NO_DATA_MESSAGE;
        }
    };

    const handleOnSelect = (row: IMyFilesFileModel, isSelect: boolean) => {
        let selectedFilesCopy = [...selectedFiles];
        if (isSelect) {
            selectedFilesCopy.push(row);
        } else {
            selectedFilesCopy = selectedFilesCopy.filter(file => file.rowId != row.rowId);
        }
        setSelectedFiles(selectedFilesCopy);
    };

    const handleOnSelectAll = (isSelect: boolean, rows: IMyFilesFileModel[]) => {
        if (isSelect) {
            setSelectedFiles(rows);
        } else {
            setSelectedFiles([]);
        }
    };

    const selectRow: SelectRowProps<any> = {
        mode: "checkbox",
        clickToSelect: true,
        nonSelectable: [],
        selected: selectedFiles.map(file => file.rowId),
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll,
        selectionHeaderRenderer: ({ indeterminate }) => (
            <Checkbox
                id="mpiCheckbox"
                size={CheckBoxSize.sm}
                checked={!!selectedFiles.length}
                indeterminate={indeterminate}
                onClick={e => e.stopPropagation()}
                onChange={e => e.stopPropagation()}
                text={""}
            />
        ),
        headerColumnStyle: { width: "36px", minWidth: "36px", maxWidth: "36px", textAlign: "center" }, // Use columnStyle for body+header
    };

    return (
        <LoadingOverlay className="file-view-loader" style={{ height: getFileViewLoaderHeight(mailboxUserData) }}>
            <div
                id="archive-file-table"
                className=" archive-table-container"
                style={{ maxHeight: getFileViewWrapperHeight(mailboxUserData) }}
            >
                <BootstrapTable
                    classes={"settings-table"}
                    keyField={"rowId"}
                    columns={columns}
                    data={fileData}
                    noDataIndication={setNoContent()}
                    bordered={false}
                    selectRow={selectRow}
                    sort={props.sortBy}
                    pagination={paginationFactory(paginationOptions)}
                    remote={{ sort: true, pagination: true }}
                    onTableChange={(type: string, { sortField, sortOrder, page }: any) => {
                        if (type === "sort") {
                            props.setSortBy && props.setSortBy({ dataField: sortField, order: sortOrder });
                        }
                        handlePageChange(page);
                    }}
                />
            </div>
            <Loader loading={isLoading} />
        </LoadingOverlay>
    );
};

export default ArchiveFileTable;
